import React, { useEffect, useRef, useMemo } from 'react';
import {
    ombreRenderer,
    type UniformsMap,
    createUniformsMap
} from '@ombre-ui/core';
// @ts-ignore - replaced at build time
import { componentDefinition, type ComponentProps } from '@ombre-ui/core/vapor6';

/**
 * Tells us the shape of the props we accept:
 * - partial overrides from the shader definition
 * - optional children
 * - optional ref for advanced usage in React 19
 */
type OmbreComponentProps = Partial<ComponentProps> & {
    children?: React.ReactNode;
    ref?: React.Ref<any>;
};

/**
 * Merges the default props and any explicit user props together into one object.
 */
function computeEffectiveProps(
    props: OmbreComponentProps,
    defaultProps: Record<string, any>
): Record<string, any> {
    let baseProps = { ...defaultProps };

    for (const [key, value] of Object.entries(props)) {
        if (key !== 'children' && key !== 'ref' && value !== undefined) {
            baseProps[key] = value;
        }
    }

    return baseProps;
}

// Store default props safely to avoid errors in different React versions
let defaultProps: Record<string, any> = {};
try {
    // Safely extract default values from shader definition
    if (componentDefinition && componentDefinition.props) {
        defaultProps = Object.entries(componentDefinition.props).reduce(
            (acc, [key, config]) => {
                if (config && typeof config === 'object' && 'default' in config) {
                    acc[key] = (config as any).default;
                }
                return acc;
            },
            {} as Record<string, any>
        );
    }
} catch (e) {
    console.warn('Error extracting default props:', e);
}

/**
 * The main React wrapper component
 * This component is compatible with both React 18 and 19
 */
export const OmbreComponent: React.FC<OmbreComponentProps> = (props) => {
    // Create a unique ID for this instance (used in createUniformsMap, if needed)
    const instanceId = useMemo(() => {
        return Math.random().toString(36).substring(7);
    }, []);

    // Compute final props, identical to the Vue version's `effectiveProps`
    const effectiveProps = useMemo(() => {
        return computeEffectiveProps(props, defaultProps);
    }, [props]);

    // References to the DOM elements
    const containerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    // We'll store the renderer and uniforms in refs so we can mutate them
    const rendererRef = useRef<ReturnType<typeof ombreRenderer> | null>(null);
    const uniformsRef = useRef<UniformsMap | null>(null);

    /**
     * 1) Set up the renderer once, on mount.
     *    In React's Strict Mode dev environment, this effect may be run in a
     *    "test" mount/unmount cycle, but by default we only initialize once
     *    (we do not re-run on prop changes).
     */
    useEffect(() => {
        // Skip initialization if elements aren't ready
        if (!canvasRef.current || !containerRef.current) return;

        // Skip if component definition isn't properly available
        if (!componentDefinition || typeof componentDefinition.fragmentNode !== 'function') {
            console.warn('Component definition not properly initialized');
            return;
        }

        // Create a new renderer and store it
        const renderer = ombreRenderer();
        rendererRef.current = renderer;

        try {
            // Create the initial uniforms map from effective props
            uniformsRef.current = createUniformsMap(
                componentDefinition,
                effectiveProps,
                instanceId
            );

            // Create the fragment node using the uniforms
            const fragmentNode = componentDefinition.fragmentNode(
                uniformsRef.current
            );

            // Now initialize the renderer, hooking up the canvas, container, etc.
            renderer
                .initialize({
                    canvas: canvasRef.current,
                    container: containerRef.current,
                    fragmentNode
                })
                .catch((error) => {
                    console.error('Failed to initialize ombreRenderer:', error);
                });
        } catch (error) {
            console.error('Error during renderer setup:', error);
        }

        // Cleanup on unmount
        return () => {
            if (rendererRef.current) {
                try {
                    rendererRef.current.cleanup();
                } catch (error) {
                    console.warn('Cleanup error:', error);
                }
                rendererRef.current = null;
            }
            uniformsRef.current = null;
        };
        // Empty dependency array => run only on first mount/unmount
    }, []);

    /**
     * 2) Whenever effectiveProps changes, we update the uniforms in place
     *    (rather than re-initializing the renderer).
     *    This is equivalent to the Vue watchers for each prop that sets
     *    the corresponding uniform value.
     */
    useEffect(() => {
        if (!uniformsRef.current) return;

        try {
            // Update each uniform with the new value from effectiveProps
            for (const [propName, uniformData] of Object.entries(uniformsRef.current)) {
                if (!uniformData || typeof uniformData !== 'object') continue;

                const { uniform, transform } = uniformData;

                if (uniform?.value !== undefined && propName in effectiveProps) {
                    const newValue = effectiveProps[propName];
                    uniform.value = transform ? transform(newValue) : newValue;
                }
            }
        } catch (error) {
            console.error('Error updating uniforms:', error);
        }
    }, [effectiveProps]);

    // For React 18 compatibility, explicitly handle the ref if provided
    // but don't use forwardRef directly as it can cause issues
    if (props.ref && typeof props.ref === 'function' && canvasRef.current) {
        try {
            // Attempt to call the ref function with the canvas element
            props.ref(canvasRef.current);
        } catch (e) {
            // Silently ignore ref errors to prevent breaking in React 18
        }
    }

    return (
        <div
            ref={containerRef}
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100%',
                height: '100%',
                overflow: 'clip'
            }}
        >
            <canvas
                data-renderer="ombre"
                ref={canvasRef}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'block'
                }}
            />
            {props.children}
        </div>
    );
};

// Simple export without any version detection - using the most compatible approach
export default OmbreComponent;
